import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { guestApiClient } from '../../services/apiClient';
import { useGuestStay } from '../../hooks/useGuestStay';
import { formatCurrency } from '../../utils/currency';
import {
  ApiError, BookingDetails, CheckInPaymentPolicy, PaymentIntentResponse, SelfCheckInResult,
} from '../../types';
import { GDTop, GDDone, GDSteps, GDSpinnerScreen } from './GDBits';
import { isKioskMode } from './GDShell';
import { launchGatewayIntent } from './gatewayLaunch';

const PENDING_INTENT_KEY = 'gd_checkin_payment_intent';

interface CheckInWizardProps {
  booking: BookingDetails;
  onCheckInComplete: () => void;
}

type Step =
  | 'loading' | 'details' | 'pay' | 'confirm' | 'done' | 'front_desk'
  // Wrong-state landings (scanned check-in QR on a stay that is already
  // in-house, already finished, or hasn't started) get an explanation
  // rather than a silent bounce to the home screen.
  | 'already_in' | 'already_out' | 'too_early';

/**
 * Self check-in, three screens or fewer: details & ID → (pay, only when the
 * hotel's policy demands it) → confirm & sign → done. Payment sits BEFORE the
 * confirmation so a failed or abandoned payment leaves a resumable
 * "pending payment" state — never a success screen that isn't true. The room
 * number is only revealed once the backend confirms the gate is satisfied.
 */
const CheckInWizard: React.FC<CheckInWizardProps> = ({ booking, onCheckInComplete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status, statusQuery, policy: livePolicy, hotelInfo, currency } = useGuestStay(booking);

  // Self check-in opens on the arrival date.
  const todayStr = new Date().toLocaleDateString('en-CA');
  const checkInStr = (status?.check_in_date || booking.check_in_date || '').slice(0, 10);
  const tooEarly = !!checkInStr && todayStr < checkInStr;

  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [frontDeskMessage, setFrontDeskMessage] = useState<string>('');
  const [checkInResult, setCheckInResult] = useState<SelfCheckInResult | null>(null);
  // Policy from the freshest source: a 409/payment-confirm payload beats the cached status
  const [policyOverride, setPolicyOverride] = useState<CheckInPaymentPolicy | null>(null);
  const policy = policyOverride || livePolicy;

  // --- details form state ---
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [docType, setDocType] = useState('passport');
  const [idFile, setIdFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prefilled = useRef(false);

  useEffect(() => {
    if (status && !prefilled.current) {
      prefilled.current = true;
      setFirstName(status.guest_details?.first_name || '');
      setLastName(status.guest_details?.last_name || '');
      setEmail(status.guest_details?.email || '');
      setPhone(status.guest_details?.phone || status.guest_details?.mobile_number || booking.mobile_number || '');
    }
  }, [status, booking]);

  const idUploaded = !!status?.id_documents_uploaded;
  const signatureOnFile = !!status?.signature_uploaded || !!status?.pre_checkin_completed;
  const paymentRequired = !!policy?.payment_required;
  const dueNow = policy?.payment_required ? policy.amount_due_now : 0;

  const stepOrder = useMemo<Step[]>(
    () => (paymentRequired ? ['details', 'pay', 'confirm'] : ['details', 'confirm']),
    [paymentRequired]
  );
  const stepIndex = stepOrder.indexOf(step as any);

  // --- signature pad (plain canvas; the backend accepts a data-URL PNG) ---
  const sigCanvasRef = useRef<HTMLCanvasElement>(null);
  const [signed, setSigned] = useState(false);
  const drawing = useRef(false);

  const sigPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = sigCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };
  const sigStart = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    const ctx = sigCanvasRef.current!.getContext('2d')!;
    const { x, y } = sigPos(e);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#14213D';
    ctx.beginPath();
    ctx.moveTo(x, y);
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
  };
  const sigMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = sigCanvasRef.current!.getContext('2d')!;
    const { x, y } = sigPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setSigned(true);
  };
  const sigEnd = () => { drawing.current = false; };
  const sigClear = () => {
    const canvas = sigCanvasRef.current;
    if (canvas) canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
  };

  const refreshStatus = useCallback(async () => {
    setPolicyOverride(null);
    await queryClient.invalidateQueries({ queryKey: ['preCheckInStatus'] });
    await queryClient.invalidateQueries({ queryKey: ['billSummary'] });
  }, [queryClient]);

  // --- the actual check-in completion (single backend transition path) ---
  const completeCheckIn = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        use_pre_checkin_data: true,
        arrival_time: new Date().toISOString(),
      };
      if (signed && sigCanvasRef.current) {
        payload.signature_image = sigCanvasRef.current.toDataURL('image/png');
      }
      const response = await guestApiClient.post<{ success: boolean; data: SelfCheckInResult }>(
        '/api/guest/check-in/', payload
      );
      setCheckInResult(response.data.data);
      setStep('done');
      onCheckInComplete();
      refreshStatus();
      return true;
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.code === 'PAYMENT_REQUIRED_BEFORE_CHECKIN') {
        if (apiError.body?.payment_policy) setPolicyOverride(apiError.body.payment_policy);
        setStep('pay');
      } else if (apiError.code === 'ROOM_NOT_READY' || apiError.code === 'GROUP_CHECKIN_FRONT_DESK') {
        setFrontDeskMessage(apiError.error);
        setStep('front_desk');
      } else if (apiError.details && typeof apiError.details === 'object') {
        const flat = Object.values(apiError.details).flat().join(' ');
        setError(flat || apiError.error);
        setStep('confirm');
      } else {
        setError(apiError.error || t('gd.checkin.genericError'));
      }
      return false;
    } finally {
      setBusy(false);
    }
  }, [signed, onCheckInComplete, refreshStatus, t]);

  // --- gateway return + lost-redirect recovery ---
  const returnHandled = useRef(false);
  useEffect(() => {
    if (returnHandled.current || !status) return;
    const params = new URLSearchParams(window.location.search);
    const intentFromUrl = params.get('payment_intent_id');
    const returnStatus = params.get('status');
    const storedIntent = localStorage.getItem(PENDING_INTENT_KEY);

    const confirmIntent = async (intentId: string) => {
      setStep('loading');
      try {
        const response = await guestApiClient.get<{ success: boolean; data: any }>(
          `/api/guest/pre-checkin/payment-status/${intentId}/`
        );
        localStorage.removeItem(PENDING_INTENT_KEY);
        const data = response.data.data || {};
        if (data.payment_policy) setPolicyOverride(data.payment_policy);
        if (data.checkin_unblocked) {
          // Paid and the date is here — finish through the one real path.
          const ok = await completeCheckIn();
          if (!ok) await refreshStatus();
        } else {
          await refreshStatus();
          setStep('confirm');
        }
      } catch {
        // Payment didn't land (abandoned session or genuine failure):
        // stay resumable on the pay step.
        localStorage.removeItem(PENDING_INTENT_KEY);
        await refreshStatus();
        setError(t('gd.checkin.paymentNotCompleted'));
        setStep('pay');
      }
    };

    if (intentFromUrl) {
      returnHandled.current = true;
      window.history.replaceState({}, '', window.location.pathname);
      if (returnStatus === 'success') {
        confirmIntent(intentFromUrl);
      } else {
        localStorage.removeItem(PENDING_INTENT_KEY);
        setError(t('gd.checkin.paymentCancelled'));
        setStep('pay');
      }
      return;
    }
    if (storedIntent && status.checkin_state === 'pending_payment') {
      // The guest paid but never made it back (closed tab mid-redirect);
      // there is no webhook, so re-confirm the stored session once.
      returnHandled.current = true;
      confirmIntent(storedIntent);
    }
  }, [status, completeCheckIn, refreshStatus, t]);

  // --- initial step routing once status arrives ---
  useEffect(() => {
    if (!status || step !== 'loading' || returnHandled.current) return;
    const reservationStatus = (status as any).reservation_status;
    if (reservationStatus === 'checked_out' || status.checkin_state === 'checked_out') {
      setStep('already_out');
    } else if (status.is_checked_in) {
      setStep('already_in');
    } else if (tooEarly) {
      setStep('too_early');
    } else if (status.checkin_state === 'pending_payment') {
      setStep('pay');
    } else {
      setStep('details');
    }
  }, [status, step, tooEarly]);

  // The desk can settle the balance while the guest sits on the pay step
  // (or a refetch may reveal an earlier payment) — advance instead of
  // showing a dead "Pay ₹0" button.
  useEffect(() => {
    if (step === 'pay' && policy && !policy.payment_required) {
      setStep('confirm');
    }
  }, [step, policy]);

  // --- details submit ---
  const submitDetails = async () => {
    setBusy(true);
    setError(null);
    try {
      await guestApiClient.post('/api/guest/pre-checkin/update-profile/', {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      if (idFile) {
        await guestApiClient.uploadMultipleGuestDocuments([{
          file: idFile,
          document_type: docType,
          document_number: `${docType.toUpperCase()}-${Date.now()}`,
          guest_name: `${firstName} ${lastName}`.trim(),
          guest_relation: 'self',
        }]);
        await refreshStatus();
      }
      setStep(paymentRequired ? 'pay' : 'confirm');
    } catch (err) {
      setError((err as ApiError).error || t('gd.checkin.genericError'));
    } finally {
      setBusy(false);
    }
  };

  // --- payment ---
  const startPayment = async () => {
    if (!policy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await guestApiClient.post<{ success: boolean; data: PaymentIntentResponse }>(
        '/api/guest/pre-checkin/create-payment-intent/',
        {
          amount: dueNow,
          payment_type: 'advance_payment',
          return_url: window.location.origin + '/checkin',
        }
      );
      const intent: any = (response.data as any).data || response.data;
      if (intent.payment_intent_id) {
        localStorage.setItem(PENDING_INTENT_KEY, intent.payment_intent_id);
      }
      launchGatewayIntent(intent, {
        returnPath: '/checkin',
        hotelName: hotelInfo?.hotel?.name || 'Hotel',
        description: t('gd.checkin.payDescription'),
        onDismiss: () => setBusy(false),
        onError: () => { setError(t('gd.checkin.noGateway')); setBusy(false); },
      });
    } catch (err) {
      setError((err as ApiError).error || t('gd.checkin.genericError'));
      setBusy(false);
    }
  };

  // Kiosk "pay at reception": record the attempt (backend flips the booking
  // to pending_payment) so the desk payment auto-unblocks the check-in.
  const payAtReception = async () => {
    setBusy(true);
    try {
      await guestApiClient.post('/api/guest/check-in/', {
        use_pre_checkin_data: true,
        arrival_time: new Date().toISOString(),
      });
      // Policy must have been satisfied after all — rare desk race; refresh.
      await refreshStatus();
      setStep('confirm');
    } catch {
      setFrontDeskMessage(t('gd.checkin.payAtReceptionSaved'));
      setStep('front_desk');
      refreshStatus();
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- render
  if (step === 'loading' || (statusQuery.isLoading && !status)) {
    return <GDSpinnerScreen label={t('gd.common.loading')} />;
  }

  const currencyOf = (amount: number) => formatCurrency(amount, currency);
  const wizardTitle =
    hotelInfo?.hotel?.button_labels?.['checkin'] || t('gd.checkin.title');

  if (step === 'front_desk') {
    return (
      <section className="gd-screen">
        <GDDone tone="orange" icon="🛎️" title={t('gd.checkin.frontDeskTitle')}>
          <p>{frontDeskMessage}</p>
          <button type="button" className="gd-btn" onClick={() => navigate('/home')}>
            {t('gd.common.backHome')}
          </button>
        </GDDone>
      </section>
    );
  }

  if (step === 'already_in') {
    const selfCheckoutOn = (hotelInfo?.hotel?.features as any)?.self_checkout === true;
    return (
      <section className="gd-screen">
        <GDDone icon="✓" title={t('gd.checkin.alreadyInTitle', 'You are already checked in')}>
          <p>
            {booking.room_number
              ? t('gd.checkin.alreadyInRoom', { room: booking.room_number, defaultValue: 'You are checked in — room {{room}}. Enjoy your stay!' })
              : t('gd.checkin.alreadyInBody', 'You are checked in. Enjoy your stay!')}
          </p>
          {selfCheckoutOn && (
            <button type="button" className="gd-btn" onClick={() => navigate('/checkout')}>
              {t('gd.checkin.goCheckout', 'Go to check-out')}
            </button>
          )}
          <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
            {t('gd.common.backHome')}
          </button>
        </GDDone>
      </section>
    );
  }

  if (step === 'already_out') {
    return (
      <section className="gd-screen">
        <GDDone tone="orange" icon="👋" title={t('gd.checkin.alreadyOutTitle', 'This stay has already ended')}>
          <p>
            {t('gd.checkin.alreadyOutBody', 'This booking is already checked out. Please contact reception if you need anything.')}
          </p>
          <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
            {t('gd.common.backHome')}
          </button>
        </GDDone>
      </section>
    );
  }

  if (step === 'too_early') {
    const arrival = checkInStr
      ? new Date(`${checkInStr}T00:00:00`).toLocaleDateString(undefined, {
          weekday: 'short', day: 'numeric', month: 'short',
        })
      : '';
    return (
      <section className="gd-screen">
        <GDDone tone="orange" icon="🗓️" title={t('gd.checkin.tooEarlyTitle', 'Check-in is not open yet')}>
          <p>
            {arrival
              ? t('gd.checkin.tooEarlyBody', { date: arrival, defaultValue: 'Check-in opens on {{date}}, your arrival date.' })
              : t('gd.checkin.tooEarlyBodyNoDate', 'Check-in opens on your arrival date.')}
          </p>
          <button type="button" className="gd-btn gd-ghost" onClick={() => navigate('/home')}>
            {t('gd.common.backHome')}
          </button>
        </GDDone>
      </section>
    );
  }

  if (step === 'done') {
    const rooms = checkInResult?.room_numbers?.length
      ? checkInResult.room_numbers.join(', ')
      : checkInResult?.room_number || booking.room_number || '';
    return (
      <section className="gd-screen">
        <GDDone title={t('gd.checkin.doneTitle')}>
          {rooms ? (
            <>
              <p>{t('gd.checkin.doneRoomIntro')}</p>
              <div className="gd-big">{rooms}</div>
            </>
          ) : (
            <p>{t('gd.checkin.doneRoomPending')}</p>
          )}
          <p>{t('gd.checkin.doneNext')}</p>
          <button type="button" className="gd-btn" onClick={() => navigate('/home')}>
            {t('gd.common.backHome')}
          </button>
        </GDDone>
      </section>
    );
  }

  return (
    <section className="gd-screen">
      <GDTop title={wizardTitle} backTo="/home" onBack={
        step === 'confirm' ? () => setStep(paymentRequired ? 'pay' : 'details')
          : step === 'pay' ? () => setStep('details')
            : undefined
      } />
      <div className="gd-body">
        <GDSteps total={stepOrder.length} current={stepIndex + 1} />
        <p className="gd-muted">
          {t('gd.checkin.stepOf', { current: stepIndex + 1, total: stepOrder.length })}
          {' · '}
          {step === 'details' && t('gd.checkin.stepDetails')}
          {step === 'pay' && t('gd.checkin.stepPay')}
          {step === 'confirm' && t('gd.checkin.stepConfirm')}
        </p>

        {error && <div className="gd-note" style={{ background: 'var(--gd-red-soft)', color: 'var(--gd-red)' }}>{error}</div>}

        {step === 'details' && (
          <>
            <div className="gd-field">
              <label>{t('gd.checkin.firstName')}</label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} autoComplete="given-name" />
            </div>
            <div className="gd-field">
              <label>{t('gd.checkin.lastName')}</label>
              <input value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="family-name" />
            </div>
            <div className="gd-field">
              <label>{t('gd.checkin.email')}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
            </div>
            <div className="gd-field">
              <label>{t('gd.checkin.phone')}</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" />
            </div>
            <div className="gd-field">
              <label>{t('gd.checkin.idType')}</label>
              <select value={docType} onChange={e => setDocType(e.target.value)}>
                <option value="passport">{t('gd.checkin.idPassport')}</option>
                <option value="national_id">{t('gd.checkin.idNational')}</option>
                <option value="aadhar_card">{t('gd.checkin.idAadhaar')}</option>
                <option value="drivers_license">{t('gd.checkin.idLicence')}</option>
              </select>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              style={{ display: 'none' }}
              onChange={e => setIdFile(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              className={`gd-upload${idFile || idUploaded ? ' gd-done' : ''}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <b>
                {idFile
                  ? idFile.name
                  : idUploaded
                    ? t('gd.checkin.idUploadedAlready')
                    : t('gd.checkin.idUploadTitle')}
              </b>
              {idFile || idUploaded ? t('gd.checkin.idReplaceHint') : t('gd.checkin.idUploadHint')}
            </button>
            <button
              type="button"
              className="gd-btn"
              disabled={busy || (!idUploaded && !idFile)}
              onClick={submitDetails}
            >
              {busy ? t('gd.common.working') : t('gd.common.continue')}
            </button>
          </>
        )}

        {step === 'pay' && policy && (
          <>
            <div className="gd-card">
              <p className="gd-muted">
                {policy.policy === 'deposit_before_checkin'
                  ? t('gd.checkin.depositIntro')
                  : t('gd.checkin.fullIntro')}
              </p>
              <div className="gd-amount">{currencyOf(dueNow)}</div>
              <div className="gd-list" style={{ marginTop: 8, fontSize: 13 }}>
                <div className="gd-item">
                  <span>{t('gd.checkin.billTotal')}</span>
                  <span style={{ marginLeft: 'auto' }}>{currencyOf(policy.grand_total)}</span>
                </div>
                {policy.total_paid > 0 && (
                  <div className="gd-item">
                    <span>{t('gd.checkin.alreadyPaid')}</span>
                    <span style={{ marginLeft: 'auto', color: 'var(--gd-green)' }}>
                      − {currencyOf(policy.total_paid)}
                    </span>
                  </div>
                )}
                {policy.policy === 'deposit_before_checkin' && (
                  <div className="gd-item">
                    <span>{t('gd.checkin.balanceAtCheckout')}</span>
                    <span style={{ marginLeft: 'auto' }}>
                      {currencyOf(Math.max(policy.balance_due - dueNow, 0))}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="gd-note gd-blue">{t('gd.checkin.payNote')}</div>
            <button type="button" className="gd-btn" disabled={busy || dueNow <= 0} onClick={startPayment}>
              {busy ? t('gd.common.working') : t('gd.checkin.payButton', { amount: currencyOf(dueNow) })}
            </button>
            {isKioskMode() && policy.kiosk_pay_at_reception && (
              <button type="button" className="gd-btn gd-plain" disabled={busy} onClick={payAtReception}>
                {t('gd.checkin.payAtReception')}
              </button>
            )}
            {!isKioskMode() && (
              <p className="gd-muted" style={{ textAlign: 'center' }}>
                {t('gd.checkin.deskFallback')}
              </p>
            )}
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="gd-card gd-list">
              <div className="gd-item">
                <div>
                  <b>{`${firstName} ${lastName}`.trim() || booking.guest_name}</b>
                  <small>
                    {idUploaded || idFile ? t('gd.checkin.idOk') : t('gd.checkin.idMissing')}
                  </small>
                </div>
                {(idUploaded || idFile) && <span className="gd-pill" style={{ marginLeft: 'auto' }}>✓</span>}
              </div>
              <div className="gd-item">
                <div>
                  <b>{booking.room_type}{booking.room_number ? ` · ${booking.room_number}` : ''}</b>
                  <small>
                    {status?.check_in_date} → {status?.check_out_date}
                    {status?.reservation_details
                      ? ` · ${t('gd.checkin.guests', { count: status.reservation_details.total_guests })}`
                      : ''}
                  </small>
                </div>
              </div>
              {policy && (
                <div className="gd-item">
                  <div>
                    <b>{t('gd.checkin.balanceDue')}</b>
                    <small>
                      {policy.prepaid_source
                        ? t('gd.checkin.prepaidVia', { source: policy.prepaid_source })
                        : policy.balance_due > 0
                          ? t('gd.checkin.payLater')
                          : t('gd.checkin.settled')}
                    </small>
                  </div>
                  <b style={{ marginLeft: 'auto' }}>
                    {policy.prepaid_source ? '✓' : currencyOf(policy.balance_due)}
                  </b>
                </div>
              )}
            </div>

            {!signatureOnFile && (
              <div className="gd-card">
                <div className="gd-row" style={{ marginBottom: 8 }}>
                  <b style={{ fontSize: 14 }}>{t('gd.checkin.signTitle')}</b>
                  <button
                    type="button"
                    onClick={sigClear}
                    style={{
                      font: 'inherit', fontSize: 12, fontWeight: 700, color: 'var(--gd-blue)',
                      background: 'none', border: 0, cursor: 'pointer',
                    }}
                  >
                    {t('gd.checkin.signClear')}
                  </button>
                </div>
                {/* touchAction:'none' is required for drawing — without it
                    the browser pans instead of inking. The cost is that a
                    guest who swipes ON the pad cannot scroll, which reads
                    as "the page is stuck". Mitigated by keeping the pad
                    short (110px) with scrollable padding either side, and
                    touchAction:'pan-y' would break drawing, so the fix is
                    the surrounding layout rather than this property. */}
                <canvas
                  ref={sigCanvasRef}
                  width={640}
                  height={220}
                  style={{
                    width: '100%', height: 110, border: '1px dashed var(--gd-line)',
                    borderRadius: 9, touchAction: 'none', background: '#fff',
                  }}
                  onPointerDown={sigStart}
                  onPointerMove={sigMove}
                  onPointerUp={sigEnd}
                  onPointerLeave={sigEnd}
                />
                <p className="gd-muted" style={{ marginTop: 8 }}>{t('gd.checkin.signAgree')}</p>
              </div>
            )}
            {signatureOnFile && (
              <div className="gd-note gd-blue">{t('gd.checkin.rulesAgreed')}</div>
            )}
            <button
              type="button"
              onClick={() => navigate('/info')}
              style={{
                font: 'inherit', fontWeight: 700, color: 'var(--gd-blue)', background: 'none',
                border: 0, cursor: 'pointer', textAlign: 'left', padding: 0,
              }}
            >
              {t('gd.checkin.readRules')}
            </button>
            <button
              type="button"
              className="gd-btn"
              disabled={busy || (!signatureOnFile && !signed)}
              onClick={completeCheckIn}
            >
              {busy ? t('gd.common.working') : t('gd.checkin.completeButton')}
            </button>
          </>
        )}
      </div>
    </section>
  );
};

export default CheckInWizard;
